import React, { useState, useEffect } from 'react';
import { Trophy, MapPin, Crown, Medal, Share2 } from 'lucide-react';
import './Leaderboard.css';

const Leaderboard = () => {
  const [cities, setCities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);


  // Mock data - replace with actual API call
  useEffect(() => {
    const fetchCities = async () => {
        const response = await fetch('https://api-toymz7ykda-uc.a.run.app/cities', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
        });
        const data = await response.json();
        console.log(data)
        setCities(data.cities);
        setTotalUsers(data.cities.reduce((sum, city) => sum + city.count, 0));
        setIsLoading(false);
    }
    fetchCities()
  }, []);

  // Get rank styling
  const getRankStyling = (rank) => {
    switch(rank) {
      case 1:
        return 'rank-first';
      case 2:
        return 'rank-second';
      case 3:
        return 'rank-third';
      default:
        return 'rank-default';
    }
  };

  // Get rank icon
  const getRankIcon = (rank) => {
    switch(rank) {
      case 1:
        return <Crown className="rank-icon crown" />;
      case 2:
        return <Trophy className="rank-icon trophy" />;
      case 3:
        return <Medal className="rank-icon medal" />;
      default:
        return <span className="rank-number">{rank}</span>;
    }
  };

  // Handle share functionality
  const handleShare = async () => {
    const shareData = {
      title: 'Our Vibe City Leaderboard',
      text: `Check out which cities are racing to become the launch city for Our Vibe! ${cities[0]?.city} is currently leading with ${formatNumber(cities[0]?.count)} verified students.`,
      url: window.location.href
    };

    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Error sharing:', err);
        setShowShareModal(true);
      }
    } else {
      setShowShareModal(true);
    }
  };

  // Copy link to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowShareModal(false);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // Format large numbers (e.g., 1000 -> 1K, 1500 -> 1.5K, 1000000 -> 1M)
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(num % 1000000 === 0 ? 0 : 1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1) + 'K';
    }
    return num.toString();
  };

  return (
    <div className="leaderboard-container">
      {/* Navigation */}
      <nav className="navbar">
        <div className="container navbar-container">
          <a href="/" className="logo">
            our<span className="logo-bold">vibe</span>
          </a>
          
          <div className="nav-links">
            <button onClick={handleShare} className="share-button">
              <Share2 size={18} />
              <span>Share</span>
            </button>
            <a href="/" className="nav-link">Home</a>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="main-content">
        <div className="container leaderboard-content">
          {/* Header Section */}
          <div className="header-section">
            <div className="title-glow"></div>
            
            <h1 className="page-title">
              <span className="title-line">City</span>
              <span className="title-gradient">Leaderboard</span>
            </h1>
            
            <p className="page-subtitle">
              The race to become the launch city for Our Vibe. 
              Verify your .edu email to help your city climb the ranks.
            </p>

            <div className="stats-container">
              <div className="stat-item">
                <div className="stat-number">{formatNumber(totalUsers)}</div>
                <div className="stat-label">Total Verified</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <div className="stat-number">{cities.length}</div>
                <div className="stat-label">Cities Competing</div>
              </div>
            </div>
          </div>

          {/* Leaderboard */}
          <div className="leaderboard-section">
            {isLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="loading-text">Loading leaderboard...</p>
              </div>
            ) : (
              <div className="leaderboard-grid">
                {cities.map((city, index) => (
                  <div 
                    key={city.city} 
                    className={`leaderboard-item ${getRankStyling(city.rank)}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="item-glow"></div>
                    
                    <div className="rank-section">
                      {getRankIcon(city.rank)}
                    </div>
                    
                    <div className="city-section">
                      <div className="city-icon">
                        <MapPin size={20} />
                      </div>
                      <div className="city-info">
                        <h3 className="city-name">{city.city}</h3>
                        <div className="city-progress">
                          <div 
                            className="progress-bar"
                            style={{ 
                              width: `${(city.count / cities[0].count) * 100}%`,
                              animationDelay: `${index * 0.2 + 0.5}s`
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="count-section">
                      <span className="count-number">{formatNumber(city.count)}</span>
                      <span className="count-label">verified</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Call to Action */}
          <div className="cta-section">
            <div className="cta-card">
              <div className="cta-glow"></div>
              <h3 className="cta-title">Don't see your city?</h3>
              <p className="cta-subtitle">Help put your city on the map</p>
              <a href="/" className="cta-button">
                Join the Race
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Share Modal */}
      {showShareModal && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-glow"></div>
            <h3 className="modal-title">Share Leaderboard</h3>
            <p className="modal-subtitle">Spread the word about the city race!</p>
            
            <div className="share-options">
              <button 
                onClick={copyToClipboard}
                className="share-option"
              >
                <div className="share-icon">📋</div>
                <span>Copy Link</span>
              </button>
              
              <a 
                href={`https://twitter.com/intent/tweet?text=Check out which cities are racing to become the launch city for Our Vibe! ${cities[0]?.city} is currently leading with ${formatNumber(cities[0]?.count)} verified students.&url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-option"
              >
                <div className="share-icon">🐦</div>
                <span>Tweet</span>
              </a>
              
              <a 
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-option"
              >
                <div className="share-icon">📘</div>
                <span>Facebook</span>
              </a>
            </div>
            
            <button 
              onClick={() => setShowShareModal(false)}
              className="modal-close"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="footer">
        <div className="container footer-container">
          <div className="footer-logo-container">
            <div className="footer-logo">
              our<span className="footer-logo-bold">vibe</span>
            </div>
          </div>
          <div className="copyright">
            &copy; {new Date().getFullYear()} Our Vibe
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Leaderboard;